import React, { FC, memo, useCallback, useEffect, useRef } from "react";
import {
  setInitializing,
  setProcessing,
  setReady,
  setText,
} from "@store/avatarSlice";
import { AzureAvatarConfig } from "@models/avatar-config";
import styled from "@emotion/styled";
import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import useStreaming from "@components/AzureAvatar/useStreaming";
import useBatching from "@components/AzureAvatar/useBatching";
import { useDispatch, useSelector } from "react-redux";
import { useRecordVoice } from "@hooks/useRecordVoice";
import { RootState } from "@store/store";
import { EventType } from "@models/audio-message";
import { useNavigate } from "react-router-dom";
import { setMessage } from "@store/snackbarSlice";
import { useTranslation } from "react-i18next";
import { applyMediaSinkId } from "@utils/media-output";

const Video = styled.video`
  width: 100%;
  height: 100%;
`;

export interface AzureAvatarProps {
  sessionId: string;
  photoId?: string;
  /** Mic / server context for the visible slide; falls back to `photoId` when omitted. */
  voiceContextPhotoId?: string;
  /** Bumps on each gallery slide so TTS can stop immediately (before debounced `photoId`). */
  gallerySlideEpoch?: number;
  avatarConfig: AzureAvatarConfig;
  streaming: boolean;
  companionName: string;
}

const AzureAvatar: FC<AzureAvatarProps> = ({
  sessionId,
  photoId,
  voiceContextPhotoId,
  gallerySlideEpoch,
  avatarConfig,
  streaming,
  companionName,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const avatarRef = useRef<AzureAvatarService | null>(null);
  const dispatch = useDispatch();
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const debouncedPhotoIdRef = useRef<string | undefined>();
  const voiceContextPhotoIdRef = useRef<string | undefined>();
  voiceContextPhotoIdRef.current = voiceContextPhotoId ?? photoId;
  const ttsInterruptGenRef = useRef(0);
  const prevGallerySlideEpochRef = useRef<number | undefined>(undefined);
  const navigate = useNavigate();
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const { t } = useTranslation();
  const outputVolume = useSelector(
    (state: RootState) => state.avatar.outputVolume,
  );
  const audioOutputDeviceId = useSelector(
    (state: RootState) => state.avatar.audioOutputDeviceId,
  );

  const {
    sendMessage: sendMessageStreaming,
    interruptPlayback,
    suppressGalleryAssistantStream,
  } = useStreaming({
    sessionId,
    avatar: avatarRef,
  });

  const sendMessageBatching = useBatching({
    sessionId,
    avatar: avatarRef,
    ttsInterruptGenerationRef: ttsInterruptGenRef,
  });

  const sendMessage = useCallback(
    (
      audio?: Blob | ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ) => {
      streaming
        ? sendMessageStreaming(audio as ArrayBufferLike, photoId, eventType)
        : sendMessageBatching(audio as Blob, photoId, eventType);
    },
    [sendMessageBatching, sendMessageStreaming, streaming],
  );

  useRecordVoice(
    processing,
    (audio) =>
      sendMessage(audio, voiceContextPhotoIdRef.current, EventType.UserInput),
    streaming,
  );

  const startAvatar = async (avatar: AzureAvatarService) => {
    dispatch(setInitializing(true));
    try {
      await avatar.start(
        avatarConfig,
        videoRef.current!,
        audioRef.current!,
        sessionId,
      );
      if (avatar.running && !avatar.stopping) {
        dispatch(setInitializing(false));
        dispatch(setReady(true));
        sendMessage(undefined, photoId, EventType.NewConversation);
      }
    } catch (e) {
      dispatch(
        setMessage({
          content: t(`errors.avatarConnectionFailed`, { name: companionName }),
          severity: "error",
        }),
      );
      navigate(isPatient ? "/portal" : "/");
    }
  };

  useEffect(() => {
    if (
      photoId &&
      debouncedPhotoIdRef.current &&
      photoId !== debouncedPhotoIdRef.current
    ) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    debouncedPhotoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    if (gallerySlideEpoch === undefined) {
      return;
    }
    if (prevGallerySlideEpochRef.current === undefined) {
      prevGallerySlideEpochRef.current = gallerySlideEpoch;
      return;
    }
    if (prevGallerySlideEpochRef.current === gallerySlideEpoch) {
      return;
    }
    prevGallerySlideEpochRef.current = gallerySlideEpoch;
    ttsInterruptGenRef.current += 1;
    suppressGalleryAssistantStream();
    void interruptPlayback();
  }, [
    gallerySlideEpoch,
    interruptPlayback,
    suppressGalleryAssistantStream,
  ]);

  useEffect(() => {
    dispatch(setProcessing(true));
    const avatar = new AzureAvatarService();
    avatarRef.current = avatar;
    startAvatar(avatar);

    return () => {
      avatar.stop();
      dispatch(setReady(false));
      dispatch(setProcessing(false));
    };
  }, [avatarConfig, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const vol = outputVolume ?? 1;
    const video = videoRef.current;
    const audio = audioRef.current;
    if (video) {
      video.volume = vol;
    }
    if (audio) {
      audio.volume = vol;
    }
  }, [outputVolume]);

  useEffect(() => {
    const sinkId = (audioOutputDeviceId ?? "") || "";
    const apply = async () => {
      try {
        await applyMediaSinkId(videoRef.current, sinkId);
        await applyMediaSinkId(audioRef.current, sinkId);
      } catch (e) {
        console.warn(e);
      }
    };
    void apply();
  }, [audioOutputDeviceId]);

  return (
    <>
      <Video autoPlay playsInline ref={videoRef} />
      <audio autoPlay playsInline ref={audioRef} />
    </>
  );
};

export default memo(AzureAvatar);
